<template>
  <div
    class="relative stage-container"
    :class="{ 'full-screen': fullScreen }"
  >
    <div
      v-if="section != null"
      :style="`width:100%; ${!fullScreen ? `padding-bottom:${100 / viewportRatio}%` : ''}; ${fullScreen ? `height:100%` : ''}; position: relative;`"
    >
      <svg
        class="border border-gray-300 rounded-md absolute inset-0 overflow-hidden inline w-full h-full"
        id="svg-stage"
        xmlns="http://www.w3.org/2000/svg"
        text-rendering="optimizeSpeed"
      >
      <g>
          <g
            v-for="sec in section.sections"
            :key="`${sec.id}-g`"
          >
            <g
              v-for="graphic in sec.graphics"
              :key="graphic.id + 'gr'"
              @click.stop="graphic.flatSectionForm ? onSelectSection(sec, graphic) : ''"
              :class="{ 'selectable': graphic.flatSectionForm }"
            >
              <circle
                v-if="(!graphic.flatSectionForm || !sec.showSeats) && graphic.type == 2"
                :cx="graphic.points[0].x"
                :cy="graphic.points[0].y"
                :r="Math.sqrt((graphic.points[0].x - graphic.points[1].x) * (graphic.points[0].x - graphic.points[1].x) + (graphic.points[0].y - graphic.points[1].y) * (graphic.points[0].y - graphic.points[1].y))"
                :stroke="graphic.lineColor"
                :stroke-width="graphic.lineWidth"
                :fill="graphic.fillColor != null ? graphic.fillColor : 'none'"
              >
              </circle>
              <line
                v-if="(!graphic.flatSectionForm || !sec.showSeats) && graphic.type == 0"
                :x1="graphic.points[0].x"
                :y1="graphic.points[0].y"
                :x2="graphic.points[1].x"
                :y2="graphic.points[1].y"
                :stroke="graphic.lineColor"
                :stroke-width="graphic.lineWidth"
                :fill="graphic.fillColor != null ? graphic.fillColor : 'none'"
              />
              <polygon
                v-if="(!graphic.flatSectionForm || !sec.showSeats) && (graphic.type == 3 || graphic.type == 4)"
                :fill="getFillSection(sec, graphic)"
                :stroke="graphic.lineColor"
                :stroke-width="graphic.lineWidth"
                :points="getPolyPoints(graphic)"
                :key="`g-${sec.id}`"
                :id="graphic.id + 'gr'"
              />
              <path
                v-if="(!graphic.flatSectionForm || !sec.showSeats) && graphic.type == 1"
                :id="graphic.id + 'gr'"
                :d="getArcPath(graphic)"
                :stroke="graphic.lineColor"
                :stroke-width="graphic.lineWidth"
                :fill="graphic.fillColor != null ? graphic.fillColor : 'none'"
              />
            </g>
          </g>
          <g
            v-for="sec in section.sections"
            :key="`${sec.id}`"
          >
            <g v-if="sec.showSeats && sec.type !== 2">
              <g
                v-for="seat in sec.seats"
                :key="seat.id"
                @click.prevent="seat.canSelect && onSelectSeat(seat)"
              >
                <circle
                  :transform="`translate(${seat.x}, ${seat.y})`"
                  :cx="0"
                  :cy="0"
                  :r="seatRadius"
                  :stroke="seat.strokeColor"
                  stroke-width="1.5"
                  :fill="seat.fillColor"
                  :class="{ 'selectable': seat.canSelect }"
                >
                  <animateTransform
                    v-if="seat.shouldAnimate"
                    attributeName="transform"
                    type="scale"
                    additive="sum"
                    from="1 1"
                    to="1.5 1.5"
                    beg="0s"
                    dur="1.5s"
                    repeatCount="indefinite"
                  />
                </circle>
              </g>
            </g>
          </g>
          <g
            v-for="sec in section.sections"
            :key="`${sec.id}-st`"
          >
            <g v-if="sec.showSeats && shouldShowSeatNumbers && sec.type !== 2 && (!defaultShowSeats || fewSeats)">
              <g
                v-for="seat in sec.seats"
                :key="seat.id + 't'"
                @click.prevent="seat.canSelect && onSelectSeat(seat)"
              >
                <text
                  :x="seat.x"
                  :y="seat.y"
                  text-anchor="middle"
                  :stroke="seat.labelColor"
                  :stroke-width="`${labelWidth}px`"
                  alignment-baseline="middle"
                  :class="{ 'selectable': seat.canSelect }"
                  :style="`fill: ${seat.labelColor}; font-size: ${seatNoSize}px; font-family: Arial;`"
                >
                  {{ !seat.lowVisibility ? seat.label : seat.label + '*' }}
                </text>
              </g>
            </g>
          </g>
          <g
            v-for="sec in section.sections"
            :key="`${sec.id}-l`"
          >
            <text
              v-for="(label, labelIndex) in sec.labels"
              :x="label.x"
              :y="label.y + parseInt(label.styles.size) / 3"
              :font-size="label.styles['font-size']"
              :font-weight="label.styles['font-weight']"
              :font-style="label.styles['font-style']"
              :text-decoration="label.styles['text-decoration']"
              :key="`${labelIndex}_label`"
            >
              {{ label.label }}
            </text>
          </g>
        </g>
      </svg>
    </div>
    <div
      class="absolute top-0 right-0 visualizer-controls bg-gray-100 bg-opacity-5 p-2 z-50"
    >
      <button
        v-if="!fullScreen && actualReservedSeats.length > 0"
        class="bg-transparent hover:bg-gray-200 cursor-pointer"
        @click="scrollToTickets()"
      >
      <div class="inline-flex items-center justify-center bg-blue-500 text-white rounded-md p-2">
        <span>{{ actualReservedSeats.length }}</span>
        <i data-feather="bookmark" class=""></i>
      </div>
      </button>
      <button
        v-if="!fullScreen"
        class="bg-transparent hover:bg-gray-200 p-2 rounded-md flex items-center justify-center"
        @click="toggleFullScreen()"
      >
        <i data-feather="maximize" class=""></i>
      </button>
      <button
        v-if="fullScreen"
        class="bg-transparent hover:bg-gray-200 p-2 rounded-md flex items-center justify-center"
        @click="toggleFullScreen()"
      >
        <i data-feather="minimize-2" class=""></i>
      </button>
      <button
        class="bg-transparent hover:bg-gray-200 p-2 rounded-md flex items-center justify-center"
        @click="zoomEventIn()"
      >
        <i data-feather="zoom-in" class=""></i>
      </button>
      <button
        class="bg-transparent hover:bg-gray-200 p-2 rounded-md flex items-center justify-center"
        @click="zoomEventOut()"
      >
        <i data-feather="zoom-out" class=""></i>
      </button>
      <button
        class="bg-transparent hover:bg-gray-200 p-2 rounded-md flex items-center justify-center"
        @click="zoomReset()"
      >
        <i data-feather="corner-down-left" class=""></i>
      </button>
    </div>
    <div
      v-show="fullScreen && actualReservedSeats.length > 0"
      class="absolute bottom-0 w-full bg-white border-t-4 border-blue-500 p-4"
    >
      <div class="text-center text-lg font-bold">
        {{ actualReservedSeats.length }} {{ actualReservedSeats.length > 1 ? $t('ticketPlural') : $t('ticketSingular') }} | {{ totalSeatsCost }} {{ reservedSeatsCurrency }}
      </div>
      <button
        class="w-full bg-blue-500 text-white font-bold py-2 rounded-md mt-2"
        @click="scrollToTickets()"
      >
        {{ $t('next') }}
      </button>
    </div>
    <div v-if="showingFlatSectionSelect" class="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center">
      <div class="bg-white rounded-lg p-4">
        <flat-section-select
          :selection="flatSelection"
          :selectTickets="selectFlatSection"
          :selectedTickets="noTickets"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import FlatSectionSelect from './FlatSectionSelect.vue';
import feather from 'feather-icons';

const TICKET_FREE = 0, TICKET_DIRTY = 10;
const MIN_SEATS_DIST = 22;
const REAL_ZOOM_SELECTION = 2.0;
const MAX_REAL_ZOOM = 5;
let MIN_REAL_ZOOM = 0.3;
const ZOOM_STEP = 1.5;
const REAL_ZOOM_TO_SHOW_SEAT_NUMBERS = 1;
let svg, pt;

// eslint-disable-next-line no-unused-vars
const SECTION_TYPE_ROWS = 0, SECTION_TYPE_SEATS = 1, SECTION_TYPE_FLAT = 2;

export default {
  name: 'VenueVisualizer',
  components: { FlatSectionSelect },
  created: function () {
    this.$nextTick(function () {
      this.createSVG();
    });

    // console.time('labels');
    // SET LABELS SIZE PROPS
    for (let j = 0; j < this.section.sections.length; j++) {
      const section = this.section.sections[j];
      if (section.labels !== null)
        for (let i = 0; i < section.labels.length; i++) {
          if (section.labels[i].styles['font-size'] != null) {
            const size = section.labels[i].styles['font-size'].split('px')[0];
            section.labels[i].styles['font-size'] = size + 'px';
            section.labels[i].styles.size = size;
          }
        }
    }
    // console.timeEnd('labels');
    let minDif = 99999999;
    // console.time('minDif');
    if (this.section.sections != null) {
      for (let k = 0; k < this.section.sections.length; k++) {
        const section = this.section.sections[k];
        if (section.type !== SECTION_TYPE_FLAT && section.seats != null)
          for (let i = 0; i < section.seats.length; ++i) {
            const seat = section.seats[i];
            for (let j = i + 1; j < section.seats.length; ++j) {
              const seat2 = section.seats[j];
              const dif = Math.sqrt((seat2.x - seat.x) * (seat2.x - seat.x) + (seat2.y - seat.y) * (seat2.y - seat.y));
              if (dif < minDif) {
                minDif = dif;
              }
            }
          }
        if (this.section.sections.length > 10 && minDif < 99999999) {
          this.defaultShowSeats = false;
          break;
        }
      }
    }
    // console.timeEnd('minDif');

    if (this.defaultShowSeats) {
      this.defaultShowSeats = true;
    } else {
      this.defaultShowSeats = false;
    }

    // console.time('scale');
    // scale so that min distance between seats is MIN_SEATS_DIST
    const scl = Math.round((MIN_SEATS_DIST / minDif) * 100) / 100;
    let minX = 999999, maxX = -999999, minY = 999999, maxY = -999999;
    // console.log(this.newFormat);
    let noSeats = 0;
    if (minDif > 0) {
      // scalare
      if (this.newFormat) {
        this.seatRadius = Math.round(this.initSeatRadius * scl * 100) / 100;
        this.seatNoSize = Math.round(this.initSeatNoSize * scl * 100) / 100;
        this.labelWidth = Math.round(this.initLabelWidth * scl * 100) / 100;
      }
      for (let j = 0; j < this.section.sections.length; j++) {
        const section = this.section.sections[j];
        let nsf = true;
        if (section.type !== SECTION_TYPE_FLAT && section.seats !== null)
          for (let i = 0; i < section.seats.length; i++) {
            section.seats[i].x = Math.round(section.seats[i].x * scl);
            section.seats[i].y = Math.round(section.seats[i].y * scl);
            if (section.seats[i].x > maxX) {
              maxX = section.seats[i].x;
            }
            if (section.seats[i].x < minX) {
              minX = section.seats[i].x;
            }
            if (section.seats[i].y > maxY) {
              maxY = section.seats[i].y;
            }
            if (section.seats[i].y < minY) {
              minY = section.seats[i].y;
            }
            noSeats++;
          }
        if (section.labels !== null)
          for (let i = 0; i < section.labels.length; i++) {
            if (section.labels[i].styles['font-size'] != null) {
              const size = section.labels[i].styles['font-size'].split('px')[0];
              section.labels[i].styles['font-size'] = size * scl + 'px';
              section.labels[i].styles.size = size * scl;
            }
            section.labels[i].x = section.labels[i].x * scl;
            section.labels[i].y = section.labels[i].y * scl;
          }
        if (section.graphics !== null)
          for (let i = 0; i < section.graphics.length; i++) {
            if (section.graphics[i].flatSectionForm) {
              nsf = false;
            }
            for (let j = 0; j < section.graphics[i].points.length; j++) {
              section.graphics[i].points[j].x = Math.round(section.graphics[i].points[j].x * scl * 100) / 100;
              section.graphics[i].points[j].y = Math.round(section.graphics[i].points[j].y * scl * 100) / 100;
            }
          }
        if (nsf) {
          this.defaultShowSeats = true;
        }
      }
    }

    if (noSeats > 0 && noSeats < 1000) {
      this.fewSeats = true;
    }

    this.clearReservedSeats();

    // console.timeEnd('scale');
    // console.time('extra');
    // add some extra fields for rendering
    for (let j = 0; j < this.section.sections.length; j++) {
      const section = this.section.sections[j];
      section.showSeats = this.defaultShowSeats;
      section.pcds = {};
      section.avSeats = 0;
      if (section.type !== SECTION_TYPE_FLAT && section.seats !== null)
        section.seats = section.seats.map(seat => {
          seat.fillColor = '#bdc3c7';
          seat.strokeColor = '#95a5a6';
          seat.shouldAnimate = false;
          seat.canSelect = false;
          seat.labelColor = 'black';
          seat.secLabel = section.label;
          seat.secId = section.id;

          if (seat.ticket != null && seat.ticket.status === TICKET_FREE) {
            section.avSeats++;
            section.pcds[seat.priceCat] = (section.pcds[seat.priceCat] || 0) + 1;
            if (section.pcds[seat.priceCat] != null) {
              section.pcds[seat.priceCat]++;
            } else {
              section.pcds[seat.priceCat] = 0;
            }
          }

          if (!seat.ticket || seat.ticket.status < TICKET_FREE || seat.ticket.status > TICKET_DIRTY ||
            (seat.ticket.status === TICKET_DIRTY && (!this.currentUserId || this.currentUserId !== seat.ticket.userId))) {
            seat.fillColor = '#f0f0f0';
            seat.strokeColor = '#dddddd';
          } else if (seat.ticket && seat.ticket.status === TICKET_FREE) {
            seat.fillColor = this.section.priceCatDefs[seat.priceCat].backgroundColor;
            seat.labelColor = this.section.priceCatDefs[seat.priceCat].labelColor;
            seat.strokeColor = '#111111';
            seat.canSelect = true;
          } else if (seat.ticket.status === TICKET_DIRTY && seat.ticket.userId === this.currentUserId) {
            seat.fillColor = '#e74c3c';
            seat.strokeColor = '#c0392b';
            seat.shouldAnimate = true;
            seat.canSelect = true;
            const userSeats = [];
            userSeats.push(seat);
            this.addToReservedSeats(userSeats);
          }

          return {
            ...seat
          };
        });
    }
    // console.timeEnd('extra');

    if (minX !== null && minX !== maxX) {
      const width = maxX - minX;
      const height = maxY - minY;
      this.viewportRatio = (width * 1.05) / height;
    } else {
      this.viewportRatio = 1.75;
    }

    const containerWidth = document.querySelector('.container ').offsetWidth;
    console.log('Container width:', containerWidth);
    const isSmallScreen = window.matchMedia('(max-width: 640px)').matches;
    if (isSmallScreen) {
      this.viewportRatio = Math.min(this.viewportRatio, 1.25);
    } else {
      this.viewportRatio = Math.min(this.viewportRatio, 1.75);
    } 
    //this.viewportRatio = Math.max(this.viewportRatio, (containerWidth / window.innerHeight) / 0.6);
    console.log('Viewport ratio:', this.viewportRatio);

  },
  async beforeMount() {
    console.log('beforeMount');
    await setTimeout(() => {
      console.log('beforeMount timeout');
    }, 3000);
  },
  mounted: function () {
    console.log('mounted');
    window.addEventListener('resize', this.handleResize);
  },
  data: function () {
    return {
      sections: null,
      zoom: 0.9,
      svgPanZoomInstance: null,
      isVenueBeingDragged: false,
      dragStartPosition: null,
      euclideanDistanceDraggedByUser: 0,
      viewportRatio: 2,
      defaultShowSeats: true,
      shouldShowSeatNumbers: false,
      initSeatRadius: 9.5,
      seatRadius: 9.5,
      initSeatNoSize: 10,
      seatNoSize: 10,
      initLabelWidth: 0.5,
      labelWidth: 0.5,
      showingFlatSectionSelect: false,
      flatSelection: null,
      fewSeats: false,
      fullScreen: false,
      buyTicketsWidth: null
    };
  },
  props: {
    sectionId: {
      type: Number
    },
    section: {
      type: Object,
      required: true
    },
    eventId: {
    },
    onClickSeat: {
      type: Function,
      required: true
    },
    currentUserId: {
    },
    onSelectFromFlatSection: {
      type: Function,
      required: true
    },
    findSectionCategory: {
      type: Function,
      required: true
    },
    noTickets: {
      required: true
    },
    actualReservedSeats: {
      type: Array,
      required: true
    },
    addToReservedSeats: {
      type: Function,
      required: true
    },
    clearReservedSeats: {
      type: Function,
      required: true
    },
    totalSeatsCost: {
      type: Number,
      required: true
    },
    reservedSeatsCurrency: {
      type: String,
      required: true
    },
    newFormat: {
      type: Boolean,
      required: true
    }
  },
  computed: {
    ...mapGetters({
      hasTicketsLeft: 'event/hasTicketsLeft',
      isOverdue: 'event/isOverdue'
    }),
    lastTicketsAvailable: function () {
      return this.currentInstance.ticketsLeft < 50;
    }
    /* numberOfSelectedSeatsByUser: function () {
      return 0;
      console.time('numberOfSelectedSeats');
      if (!this.currentUserId)
        return 0;

      let no = 0;
      for (let i = 0; i < this.section.sections.length; i++) {
        let sec = this.section.sections[i];
        no += sec.seats.filter(seat => {
          return seat.ticket && seat.ticket.status === TICKET_DIRTY && seat.ticket.userId === this.currentUserId
        }).length;
      }
      console.timeEnd('numberOfSelectedSeats');
      return no;
    } */
    /* allLabels: function () {
      let labels = [];

      if (this.sectionId !== -1) {
        const section = this.section.sections.find(s => s.id === this.sectionId);
        if (section)
          labels = section.labels;
      } else {
        labels = this.section.sections.reduce((all, s) => all.concat(s.labels), []);
      }

      return labels;
    },
    allGraphics: function () {
      let graphics = [];
      if (this.sectionId !== -1) {
        const section = this.section.sections.find(s => s.id === this.sectionId);
        if (section)
          graphics = section.graphics;
      } else {
        this.section.sections.forEach(section => {
          section.graphics.forEach(graphic => {
            if (graphic.flatSectionForm) {
              graphic.sectionId = section.id;
            }
            graphics.push(graphic);
          });
        });
      }
      return graphics;
    } */
  },
  methods: {
    createSVG: function () {
      // console.time('nextTick');
      svg = document.getElementById('svg-stage');
      pt = svg.createSVGPoint();
      if (document.getElementById('svg-stage')) {
        if (this.svgPanZoomInstance != null) {
          this.svgPanZoomInstance.destroy();
        }
        this.svgPanZoomInstance = (require('svg-pan-zoom'))('#svg-stage', {
          panEnabled: true,
          zoomEnabled: true,
          dblClickZoomEnabled: false,
          mouseWheelZoomEnabled: false,
          fit: false,
          maxZoom: 1000,
          minZoom: 0.01,
          beforePan: function (oldPan, newPan) {
            const sizes = this.getSizes();
            const gutterWidth = 100; // sizes.viewBox.width * sizes.realZoom;
            const gutterHeight = 100; // sizes.viewBox.height * sizes.realZoom;
            const leftLimit = -((sizes.viewBox.x + sizes.viewBox.width) * sizes.realZoom) + gutterWidth;
            const rightLimit = sizes.width - gutterWidth - (sizes.viewBox.x * sizes.realZoom);
            const topLimit = -((sizes.viewBox.y + sizes.viewBox.height) * sizes.realZoom) + gutterHeight;
            const bottomLimit = sizes.height - gutterHeight - (sizes.viewBox.y * sizes.realZoom);

            const customPan = {};
            customPan.x = Math.max(leftLimit, Math.min(rightLimit, newPan.x));
            customPan.y = Math.max(topLimit, Math.min(bottomLimit, newPan.y));
            return customPan;
          },
          customEventsHandler: {
            haltEventListeners: ['touchstart', 'touchend', 'touchmove', 'touchleave', 'touchcancel'],
            init: function (options) {
              const Hammer = require('hammerjs');

              this.hammer = Hammer(options.svgElement, {
                inputClass: Hammer.SUPPORT_POINTER_EVENTS ? Hammer.PointerEventInput : Hammer.TouchInput
              });

              const instance = options.instance;
              let initialScale = 1;
              let pannedX = 0;
              let pannedY = 0;

              this.hammer.get('pinch').set({ enable: true });

              this.hammer.on('tap', this.onNoSeatClick, false);

              this.hammer.on('panstart panmove', function (ev) {
                if (ev.type === 'panstart') {
                  pannedX = 0;
                  pannedY = 0;
                }
                that.removeSmoothTransition();
                // Pan only the difference
                instance.panBy({ x: ev.deltaX - pannedX, y: ev.deltaY - pannedY })
                pannedX = ev.deltaX
                pannedY = ev.deltaY
              })

              // Handle pinch
              this.hammer.on('pinchstart pinchmove', function (ev) {
                that.removeSmoothTransition();
                // On pinch start remember initial zoom
                const sizes = that.svgPanZoomInstance.getSizes();
                const point = that.cursorPoint(ev.center.x, ev.center.y);
                if (ev.type === 'pinchstart') {
                  initialScale = instance.getZoom();
                  // instance.zoomAtPoint(initialScale * ev.scale, point);
                }
                if (ev.scale > 1) {
                  if (sizes.realZoom >= MAX_REAL_ZOOM) {
                    return;
                  }
                } else {
                  if (sizes.realZoom <= MIN_REAL_ZOOM) {
                    return;
                  }
                }
                instance.zoomAtPoint(initialScale * ev.scale, point);
                if (that.svgPanZoomInstance.getSizes().realZoom > REAL_ZOOM_TO_SHOW_SEAT_NUMBERS) {
                  that.shouldShowSeatNumbers = true;
                } else {
                  that.shouldShowSeatNumbers = false;
                }
              })

              // Prevent moving the page on some devices when panning over SVG
              options.svgElement.addEventListener('touchmove', function (e) { e.preventDefault(); });
            },
            destroy: function () {
              this.hammer.destroy();
            }
          }
        });
        
        const that = this;
        document.getElementById('svg-stage').addEventListener('click', this.onNoSeatClick, false);
        document.getElementById('svg-stage').addEventListener('mousedown', function () {
          that.removeSmoothTransition();
          that.dragStartPosition = null;
          that.euclideanDistanceDraggedByUser = 0;
          that.isVenueBeingDragged = false;
        }, false);
        document.getElementById('svg-stage').addEventListener('mousemove', function (mouseEvent) {
          if (!that.isVenueBeingDragged) {
            that.dragStartPosition = {
              x: mouseEvent.x,
              y: mouseEvent.y
            };

            that.manhattanDistanceDraggedByUser = 0;
            that.isVenueBeingDragged = true;
          } else {
            const currentPoint = { x: mouseEvent.x, y: mouseEvent.y };

            that.manhattanDistanceDraggedByUser = that.dragStartPosition
              ? Math.abs(currentPoint.x - that.dragStartPosition.x + currentPoint.y - that.dragStartPosition.y)
              : 0;
          }
        }, false);
      }
      // console.timeEnd('nextTick');
      this.setReasonableZoom();
    },
    onSelectSeat: function (seat) {
      if (this.isVenueBeingDragged && this.manhattanDistanceDraggedByUser > 40)
        return;
      // const actualSeat = this.findActualSeat(seat);
      this.onClickSeat(seat);
      if (!seat.ticket || seat.ticket.status < TICKET_FREE || seat.ticket.status > TICKET_DIRTY ||
        (seat.ticket.status === TICKET_DIRTY && (!this.currentUserId || this.currentUserId !== seat.ticket.userId))) {
        seat.fillColor = '#f0f0f0';
        seat.strokeColor = '#dddddd';
        seat.shouldAnimate = false;
        seat.canSelect = false;
      } else if (seat.ticket && seat.ticket.status === TICKET_FREE) {
        seat.fillColor = this.section.priceCatDefs[seat.priceCat].backgroundColor;
        seat.labelColor = this.section.priceCatDefs[seat.priceCat].labelColor;
        seat.strokeColor = '#111111';
        seat.canSelect = true;
        seat.shouldAnimate = false;
      } else if (seat.ticket.status === TICKET_DIRTY && seat.ticket.userId === this.currentUserId) {
        seat.fillColor = '#e74c3c';
        seat.strokeColor = '#c0392b';
        seat.shouldAnimate = true;
        seat.canSelect = true;
      }
      if (this.shouldZoomAfterSelectTicket()) {
        this.panAndZoomAtSeat(seat);
      }
    },
    onNoSeatClick: function (ev) {
      if (this.showingFlatSectionSelect)
        return;
      const sizes = this.svgPanZoomInstance.getSizes();
      const pan = this.svgPanZoomInstance.getPan();

      const zoom = sizes.realZoom;
      const pt = this.cursorPoint(ev.clientX, ev.clientY);
      let x = pt.x;
      let y = pt.y;
      x = (x - pan.x) / zoom;
      y = (y - pan.y) / zoom;
      if (this.isVenueBeingDragged) {
        return;
      }
      if (this.shouldZoomAfterSelectTicket()) {
        this.panAndZoomAtSeat({ x, y });
      }
    },
    findActualSeat: function (seat) {
      const foundSection = this.section.sections.find(s => s.id === seat.sectionId);
      if (!foundSection)
        return null;

      return foundSection.seats.find(s => s.id === seat.id);
    },
    onSelectSection: async function (section, graphic) {
      if (this.isVenueBeingDragged && this.manhattanDistanceDraggedByUser > 40)
        return;
      // const actualSection = this.section.sections.find(s => s.id === sectionId);
      const actualSection = section;
      if (actualSection.type === 2) {
        const category = this.findSectionCategory(actualSection.id);
        this.showingFlatSectionSelect = true;
        this.flatSelection = { category, section: actualSection };
      } else {
        this.section.sections.forEach(section => {
          section.showSeats = this.defaultShowSeats;
        });
        actualSection.showSeats = true;
        // because ... the key must change
        actualSection.id = actualSection.id * -1;
        actualSection.id = actualSection.id * -1;

        if (graphic != null && graphic.flatSectionForm) {
          this.panAndZoomAtSection(section, graphic);
        }
      }
    },
    selectFlatSection: function (no) {
      this.showingFlatSectionSelect = false;
      this.onSelectFromFlatSection(no, this.flatSelection.category);
    },
    toggleFullScreen: function () {
      this.fullScreen = !this.fullScreen;
      if (this.fullScreen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
      this.$nextTick(function () {
        this.svgPanZoomInstance.resize();
        this.svgPanZoomInstance.fit();
        this.svgPanZoomInstance.zoom(this.svgPanZoomInstance.getZoom() * 0.95);
        this.svgPanZoomInstance.center();
        const realZoom = this.svgPanZoomInstance.getSizes().realZoom;
        MIN_REAL_ZOOM = realZoom;
        // this.setReasonableZoom();
        // this.zoomReset();
        if (this.fullScreen) {
          this.svgPanZoomInstance.enableMouseWheelZoom();
        } else {
          this.svgPanZoomInstance.disableMouseWheelZoom();
        }
        feather.replace();
      });
    },
    zoomEventIn: function () {
      if (!this.svgPanZoomInstance)
        return;
      const realZoom = this.svgPanZoomInstance.getSizes().realZoom;
      const toZoomScale = realZoom * ZOOM_STEP;
      if (toZoomScale < MAX_REAL_ZOOM) {
        // this.svgPanZoomInstance.resetZoom();
        this.zoomToRealZoom(toZoomScale);
      }
      // this.svgPanZoomInstance.zoomIn();
    },
    zoomEventOut: function () {
      if (!this.svgPanZoomInstance)
        return;
      const realZoom = this.svgPanZoomInstance.getSizes().realZoom;
      const toZoomScale = realZoom / ZOOM_STEP;
      if (toZoomScale > MIN_REAL_ZOOM) {
        // this.svgPanZoomInstance.resetZoom();
        this.zoomToRealZoom(toZoomScale);
      }
    },
    zoomReset: function () {
      if (!this.svgPanZoomInstance)
        return;
      this.addSmoothTransition();
      this.svgPanZoomInstance.resetZoom();
      this.svgPanZoomInstance.fit();
      this.svgPanZoomInstance.zoom(this.svgPanZoomInstance.getZoom() * 0.95);
      this.svgPanZoomInstance.center();
      if (this.svgPanZoomInstance.getSizes().realZoom > REAL_ZOOM_TO_SHOW_SEAT_NUMBERS) {
        this.shouldShowSeatNumbers = true;
      } else {
        this.shouldShowSeatNumbers = false;
      }
    },
    setReasonableZoom: function () {
      this.svgPanZoomInstance.fit();
      this.svgPanZoomInstance.zoom(this.svgPanZoomInstance.getZoom() * 0.95);
      const realZoom = this.svgPanZoomInstance.getSizes().realZoom;
      MIN_REAL_ZOOM = realZoom;
      if (realZoom > MAX_REAL_ZOOM / 2) {
        this.svgPanZoomInstance.reset();
        // this.viewportRatio = 3;
        // svg.style.height = '200px';
        // this.svgPanZoomInstance.resize();
        // this.svgPanZoomInstance.updateBBox();
        // this.svgPanZoomInstance.reset();
        // this.svgPanZoomInstance.fit();
        this.zoomToRealZoom(MAX_REAL_ZOOM / 2);
      } else {
        if (this.svgPanZoomInstance.getSizes().realZoom > REAL_ZOOM_TO_SHOW_SEAT_NUMBERS) {
          this.shouldShowSeatNumbers = true;
        } else {
          this.shouldShowSeatNumbers = false;
        }
      }
    },
    panAndZoomAtSeat: function (seat) {
      if (!this.svgPanZoomInstance || !seat)
        return;

      const section = this.getSectionById(seat.secId);
      if (section != null && !section.showSeats) {
        section.showSeats = true;
        section.id = section.id * -1;
        section.id = section.id * -1;
      }
      if (this.shouldZoomAfterSelectTicket()) {
        // this.svgPanZoomInstance.resetZoom();
        this.zoomToRealZoom(REAL_ZOOM_SELECTION);
      }
      const sizes = this.svgPanZoomInstance.getSizes();
      this.svgPanZoomInstance.pan({ x: -seat.x * sizes.realZoom + sizes.width / 2, y: -seat.y * sizes.realZoom + sizes.height / 2 });
    },
    panAndZoomAtSection: function (section, graphic) {
      if (!this.svgPanZoomInstance || !section)
        return;

      if (graphic == null) {
        return;
      }
      const polygonBoundingBox = document.getElementById('svg-stage').getElementById(graphic.id + 'gr').getBBox();
      const secC = { x: polygonBoundingBox.x + polygonBoundingBox.width / 2, y: polygonBoundingBox.y + polygonBoundingBox.height / 2 };
      let sizes = this.svgPanZoomInstance.getSizes();
      const zoomFitSection = Math.min(sizes.viewBox.height * MIN_REAL_ZOOM / polygonBoundingBox.height, sizes.viewBox.width * MIN_REAL_ZOOM / polygonBoundingBox.width);
      // this.svgPanZoomInstance.resetZoom();
      this.zoomToRealZoom(zoomFitSection);
      sizes = this.svgPanZoomInstance.getSizes();
      this.svgPanZoomInstance.pan({ x: -secC.x * sizes.realZoom + sizes.width / 2, y: -secC.y * sizes.realZoom + sizes.height / 2 });
    },
    getFillSection: function (section, graphic) {
      // graphic.fillColor != null ? graphic.fillColor : (graphic.flatSectionForm ? 'white' : 'none')
      if (graphic.flatSectionForm) {
        if (section.avSeats === 0) {
          return '#cecece';
        }
        const pcdsIds = Object.keys(section.pcds);
        if (pcdsIds.length === 0) {
          return 'white';
        }
        if (pcdsIds.length === 1) {
          return this.section.priceCatDefs[pcdsIds[0]].backgroundColor;
        }
        let max = -1, catId;
        for (let i = 0; i < pcdsIds.length; i++) {
          if (section.pcds[pcdsIds[i]] > max) {
            max = section.pcds[pcdsIds[i]];
            catId = pcdsIds[i];
          }
        }
        return this.section.priceCatDefs[catId].backgroundColor;
      } else {
        return graphic.fillColor != null ? graphic.fillColor : 'none';
      }
    },
    getSectionById: function (id) {
      return this.section.sections.find(section => section.id === id);
    },
    scrollToTarget(targetClass) {
      const targetElement = document.querySelector(`.${targetClass}`);
      if (!targetElement) return;

      const offset = targetElement.offsetTop - 60; // Adjust for any fixed headers
      window.scrollTo({ top: offset, behavior: 'smooth' });
    },
    scrollToTickets() {
      if (this.fullScreen) {
        this.toggleFullScreen();
      }
      this.scrollToTarget('selected-seats-summary');
    },
    zoomToRealZoom: async function (toRealZoom) {
      await this.addSmoothTransition();
      if (!this.svgPanZoomInstance)
        return;
      const currentRealZoom = await this.svgPanZoomInstance.getSizes().realZoom;
      const currentZoom = await this.svgPanZoomInstance.getZoom();
      const shouldZoom = toRealZoom * currentZoom / currentRealZoom;
      this.svgPanZoomInstance.zoom(shouldZoom);
      if (!this.shouldShowSeatNumbers && toRealZoom > REAL_ZOOM_TO_SHOW_SEAT_NUMBERS) {
        this.shouldShowSeatNumbers = true;
      }
      if (this.shouldShowSeatNumbers && toRealZoom < REAL_ZOOM_TO_SHOW_SEAT_NUMBERS) {
        this.shouldShowSeatNumbers = false;
      }
    },
    shouldZoomAfterSelectTicket: function () {
      if (!this.svgPanZoomInstance)
        return false;
      return this.svgPanZoomInstance.getSizes().realZoom + 0.2 < REAL_ZOOM_SELECTION;
    },
    cursorPoint: function (x, y) {
      pt.x = x;
      pt.y = y;
      return pt.matrixTransform(svg.getScreenCTM().inverse());
    },
    addSmoothTransition: function () {
      svg.getElementsByClassName('svg-pan-zoom_viewport')[0].classList.add('smooth-transition');
    },
    removeSmoothTransition: function () {
      svg.getElementsByClassName('svg-pan-zoom_viewport')[0].classList.remove('smooth-transition');
    },
    getTopLeft: function (points) {
      if (points.length === 0) {
        return null;
      }
      let y = points[0].y;
      let x = points[0].x;
      let index = 0;
      for (let i = 0; i < points.length; i++) {
        if (points[i].y <= y && points[i].x <= x) {
          y = points[i].y;
          x = points[i].x;
          index = i;
        }
      }
      return points[index];
    },
    getArcPath: function (graphic) {
      const points = graphic.points;
      if (points.length !== 4) {
        return '';
      }
      return `M ${points[0].x} ${points[0].y} C ${points[2].x} ${points[2].y} ${points[3].x} ${points[3].y} ${points[1].x} ${points[1].y}`;
      // return `M ${points[0].x + graphic.lineWidth * 2} ${points[0].y + graphic.lineWidth * 4} C ${points[2].x + graphic.lineWidth * 2} ${points[2].y + graphic.lineWidth * 4} ${points[3].x + graphic.lineWidth * 2} ${points[3].y + graphic.lineWidth * 4} ${points[1].x + graphic.lineWidth * 2} ${points[1].y + graphic.lineWidth * 4}`;
    },
    getPolyPoints: function (graphic) {
      let r = '';
      for (let i = 0; i < graphic.points.length; i++) {
        r += graphic.points[i].x + ',' + graphic.points[i].y + ' ';
      }
      return r.trim();
    },
    changeSection (section, seat) {
      this.$emit('update:modelValue', section.priceCatDefs[seat.priceCat].stats.free++)
    },
    handleResize() {
      // Handle resizing logic
      if (!this.svgPanZoomInstance) return;
      console.log('Resizing SVG Pan Zoom instance');
      this.svgPanZoomInstance.resize();
      this.svgPanZoomInstance.fit();
      this.svgPanZoomInstance.zoom(this.svgPanZoomInstance.getZoom() * 0.95);
      this.svgPanZoomInstance.center();
    }
  },
  beforeUnmount() {
    console.log('beforeUnmount');
    // Remove the resize event listener
    window.removeEventListener('resize', this.handleResize);
  },
  /* watch: {
    section (newValue) {
      console.log('section changed', newValue);
      // this.$emit('update:section', newValue)
    }
  } */
}
</script>

<style scoped>
.stage-container svg text {
  cursor: default;
  user-select: none;
}

.stage-container svg text::selection {
  background: none;
}

.smooth-transition {
  transition: transform 0.5s ease;
}

.selectable {
    cursor: pointer !important;
}

.full-screen {
  position: fixed !important;
  width: 100% !important;
  height: 100% !important;
  left: 0 !important;
  top: 0 !important;
  background: #fff;
  z-index: 2500 !important;
}
</style>